import React, { useState, useEffect } from 'react';
import { 
  Store,
  TrendingUp,
  TrendingDown,
  Award,
  Loader2,
  RefreshCw,
  ChevronRight,
  Target,
  BarChart3
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export default function StorePromotionPerformanceWidget({ showHeader = true }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStorePerformance();
  }, []);

  const fetchStorePerformance = async () => {
    try {
      const data = await apiRequest('/sales/store-promotion-performance');
      setStores(data.stores || []);
    } catch (error) {
      console.error('Failed to fetch store performance:', error);
      setStores(getMockStores());
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-blue-600 bg-blue-50';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getResponseColor = (rate) => {
    if (rate >= 75) return 'text-green-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const topPerformer = stores.length > 0 ? stores[0] : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="store-promotion-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Store size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Store Promo Performance</h3>
              <p className="text-xs text-slate-500">Response to campaigns</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchStorePerformance}>
            <RefreshCw size={16} />
          </Button>
        </div>
      )}

      {/* Top Performer Highlight */}
      {topPerformer && (
        <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Award size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-amber-600 font-medium">TOP PERFORMER</p>
              <p className="text-lg font-bold text-slate-900">{topPerformer.name}</p>
              <p className="text-sm text-slate-600">{topPerformer.response_rate}% campaign response rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Store Rankings */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3">
          {stores.map((store, idx) => (
            <div key={idx} className="p-3 rounded-xl border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                    idx === 1 ? 'bg-slate-200 text-slate-700' :
                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="font-medium text-slate-900">{store.name}</span>
                </div>
                <Badge className={getPerformanceColor(store.score)}>{store.score}/100</Badge>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className={`text-sm font-bold ${getResponseColor(store.response_rate)}`}>
                    {store.response_rate}%
                  </p>
                  <p className="text-xs text-slate-500">Response</p>
                </div>
                <div>
                  <p className={`text-sm font-bold flex items-center justify-center gap-1 ${
                    store.sales_lift >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {store.sales_lift >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {Math.abs(store.sales_lift)}%
                  </p>
                  <p className="text-xs text-slate-500">Sales Lift</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{store.campaigns_run}</p>
                  <p className="text-xs text-slate-500">Campaigns</p>
                </div>
              </div>

              {store.best_campaign && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    <span className="font-medium">Best:</span> {store.best_campaign}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getMockStores() {
  return [
    { 
      name: 'Clyde', 
      score: 92, 
      response_rate: 88, 
      sales_lift: 45, 
      campaigns_run: 12,
      best_campaign: 'Diwali Mega Sale (+52% lift)'
    },
    { 
      name: 'Hoppers Crossing', 
      score: 85, 
      response_rate: 76, 
      sales_lift: 38, 
      campaigns_run: 11,
      best_campaign: 'BOGO Snacks (+44% lift)'
    },
    { 
      name: 'Craigieburn', 
      score: 78, 
      response_rate: 71, 
      sales_lift: 32, 
      campaigns_run: 10,
      best_campaign: 'Fresh Produce Week (+35% lift)'
    },
    { 
      name: 'Melton', 
      score: 65, 
      response_rate: 58, 
      sales_lift: 22, 
      campaigns_run: 9,
      best_campaign: 'Weekly Flyer (+28% lift)'
    },
    { 
      name: 'Epping', 
      score: 58, 
      response_rate: 52, 
      sales_lift: 15, 
      campaigns_run: 8,
      best_campaign: 'Rice & Atta Bundle (+20% lift)'
    }
  ];
}
